import { useRef, useEffect, useState, useCallback } from "react";
import type { SceneCommandExecutor } from "../systems/SceneCommandExecutor";

interface Props {
  executor: SceneCommandExecutor | null;
}

interface Tooltip {
  cx: number; cy: number;   // canvas px
  name: string;
  bearing: string;
  dist: string;
}

const FRAME_SKIP    = 3;   // ~20fps
const MIN_RANGE     = 80;
const MAX_TACTICAL_RANGE = 500; // Only show entities within tactical range
const WEAPON_RANGE  = 38;  // world units — phaser effective range
const HIT_RADIUS_PX = 12;  // pixels — hover hit radius for entities
const ZOOM_STEP     = 1.15;
const MIN_ZOOM      = 0.25;
const MAX_ZOOM      = 8;

export function TacticalMap({ executor }: Props) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const execRef    = useRef(executor);
  execRef.current  = executor;

  const [tooltip, setTooltip] = useState<Tooltip | null>(null);

  // null = auto-fit, number = manual override
  const zoomRef = useRef<number | null>(null);

  // ── Compute scale for a given canvas size ──
  const getScale = useCallback((W: number, H: number, exec: SceneCommandExecutor) => {
    const spatial = exec.currentSpatialState;
    const pp = spatial.playerShip.position;
    const pad = 24;

    if (zoomRef.current !== null) {
      // Manual zoom: base scale at 1x = fit MIN_RANGE, multiplied by zoom
      const baseScale = (Math.min(W, H) / 2 - pad) / MIN_RANGE;
      return baseScale * zoomRef.current;
    }

    // Auto-fit: scale to fit all entities
    let maxDist = MIN_RANGE;
    for (const ent of spatial.entities) {
      const dx = ent.position.x - pp.x, dz = ent.position.z - pp.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < MAX_TACTICAL_RANGE && d > maxDist) maxDist = d;
    }
    return (Math.min(W, H) / 2 - pad) / maxDist;
  }, []);

  // ── Mouse wheel zoom ──
  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const exec = execRef.current;
    if (!canvas || !exec) return;

    const W = canvas.width, H = canvas.height;

    if (zoomRef.current === null) {
      // First manual zoom — compute current auto-fit level as starting point
      const spatial = exec.currentSpatialState;
      const pp = spatial.playerShip.position;
      const pad = 24;
      let maxDist = MIN_RANGE;
      for (const ent of spatial.entities) {
        const dx = ent.position.x - pp.x, dz = ent.position.z - pp.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d < MAX_TACTICAL_RANGE && d > maxDist) maxDist = d;
      }
      const baseScale = (Math.min(W, H) / 2 - pad) / MIN_RANGE;
      const autoScale = (Math.min(W, H) / 2 - pad) / maxDist;
      zoomRef.current = autoScale / baseScale;
    }

    const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    zoomRef.current = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomRef.current * factor));
  }, []);

  // ── Double-click to reset to auto-fit ──
  const handleDoubleClick = useCallback(() => {
    zoomRef.current = null;
  }, []);

  // ── Mouse / touch hover for bearing readout ──
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const exec   = execRef.current;
    if (!canvas || !exec) { setTooltip(null); return; }

    const rect  = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top)  * scaleY;

    const W = canvas.width, H = canvas.height;
    const CX = W / 2, CY = H / 2;
    const spatial = exec.currentSpatialState;
    const pp = spatial.playerShip.position;
    const scale = getScale(W, H, exec);

    for (const ent of spatial.entities) {
      const cx = CX + (ent.position.x - pp.x) * scale;
      const cy = CY + (ent.position.z - pp.z) * scale;
      if (Math.abs(mx - cx) < HIT_RADIUS_PX && Math.abs(my - cy) < HIT_RADIUS_PX) {
        const dx = ent.position.x - pp.x;
        const dz = ent.position.z - pp.z;
        const dist  = Math.sqrt(dx*dx + dz*dz).toFixed(0);
        const hd    = spatial.playerShip.heading;
        const headAngle = Math.atan2(hd.z, hd.x);
        const entAngle  = Math.atan2(dz, dx);
        let bearing = ((entAngle - headAngle) * 180 / Math.PI + 360) % 360;
        setTooltip({ cx, cy, name: ent.name, bearing: bearing.toFixed(0).padStart(3, "0"), dist });
        return;
      }
    }
    setTooltip(null);
  }, [getScale]);

  // ── Canvas drawing loop ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let animId = 0;
    let frame  = 0;

    const draw = () => {
      animId = requestAnimationFrame(draw);
      if (++frame % FRAME_SKIP !== 0) return;

      const ctx  = canvas.getContext("2d");
      const exec = execRef.current;
      if (!ctx || !exec) return;

      const W = canvas.width, H = canvas.height;
      const CX = W / 2, CY = H / 2;

      const spatial = exec.currentSpatialState;
      const pp = spatial.playerShip.position;

      const scale = getScale(W, H, exec);
      // Derive visible range from scale for ring spacing
      const pad = 24;
      const maxDist = (Math.min(W, H) / 2 - pad) / scale;

      const toC = (wx: number, wz: number) => ({
        x: CX + (wx - pp.x) * scale,
        y: CY + (wz - pp.z) * scale,
      });

      // Clear
      ctx.clearRect(0, 0, W, H);

      // ── Range rings ──
      const ringStep = maxDist < 80 ? 25 : maxDist < 160 ? 50 : 100;
      for (let r = ringStep; r <= maxDist * 1.1; r += ringStep) {
        const pr = r * scale;
        ctx.beginPath();
        ctx.arc(CX, CY, pr, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(30, 70, 120, 0.45)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.fillStyle = "rgba(60, 110, 180, 0.45)";
        ctx.font = "7px 'Courier New', monospace";
        ctx.fillText(`${r}u`, CX + pr + 2, CY - 2);
      }

      // ── Weapon range ring ──
      const wr = WEAPON_RANGE * scale;
      if (wr < Math.min(W, H) / 2) {
        ctx.beginPath();
        ctx.arc(CX, CY, wr, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(80, 160, 255, 0.25)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // ── Crosshair ──
      ctx.beginPath();
      ctx.moveTo(0, CY); ctx.lineTo(W, CY);
      ctx.moveTo(CX, 0); ctx.lineTo(CX, H);
      ctx.strokeStyle = "rgba(30, 70, 120, 0.3)";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // ── Entities ──
      for (const e of spatial.entities) {
        const dx = e.position.x - pp.x, dz = e.position.z - pp.z;
        const eDist = Math.sqrt(dx*dx + dz*dz);

        // Skip stars — too far and too large for tactical display
        if (e.type === "star") continue;

        // Distant entities: draw edge indicator arrow
        if (eDist > MAX_TACTICAL_RANGE) {
          const angle = Math.atan2(dz, dx);
          const edgeR = Math.min(W, H) / 2 - 8;
          const ex = CX + Math.cos(angle) * edgeR;
          const ey = CY + Math.sin(angle) * edgeR;
          ctx.save();
          ctx.translate(ex, ey);
          ctx.rotate(angle);
          ctx.beginPath();
          ctx.moveTo(4, 0); ctx.lineTo(-2, -3); ctx.lineTo(-2, 3);
          ctx.closePath();
          ctx.fillStyle = e.type === "planet" ? "rgba(30, 140, 120, 0.4)" : "rgba(80, 130, 200, 0.4)";
          ctx.fill();
          ctx.restore();
          continue;
        }

        const p = toC(e.position.x, e.position.z);

        if (e.type === "planet" || e.type === "jump_point") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 9, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(30, 140, 120, 0.65)";
          ctx.fill();
          ctx.strokeStyle = "#3de0c8";
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = "#6ee8d8";
          ctx.font = "8px 'Courier New', monospace";
          ctx.fillText(e.name.toUpperCase(), p.x + 12, p.y + 3);

        } else if (e.type === "ship" || e.type === "station" || e.type === "shuttle" || e.type === "debris") {
          const isHostile = e.faction === "Velan Dominion";
          const isSmall = e.type === "shuttle" || e.type === "debris";
          const stroke = isSmall ? "#88ddaa" : isHostile ? "#ff5555" : "#55aaff";
          const fill   = isSmall ? "rgba(80, 180, 120, 0.75)" : isHostile ? "rgba(200, 40, 40, 0.75)" : "rgba(40, 110, 210, 0.75)";
          const sz = isSmall ? 0.6 : 1;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.scale(sz, sz);
          ctx.beginPath();
          ctx.moveTo(0, -6); ctx.lineTo(4, 4); ctx.lineTo(0, 2); ctx.lineTo(-4, 4);
          ctx.closePath();
          ctx.fillStyle = fill;
          ctx.fill();
          ctx.strokeStyle = stroke;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.restore();

          if (e.shields === "up") {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
            ctx.strokeStyle = isHostile ? "rgba(255, 80, 80, 0.45)" : "rgba(80, 160, 255, 0.45)";
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }

          // Bearing line to hostile ship
          if (isHostile) {
            ctx.beginPath();
            ctx.moveTo(CX, CY);
            ctx.lineTo(p.x, p.y);
            ctx.strokeStyle = "rgba(220, 80, 80, 0.15)";
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }

          ctx.fillStyle = stroke;
          ctx.font = "8px 'Courier New', monospace";
          ctx.fillText(e.name.toUpperCase(), p.x + 7, p.y - 5);
        }
      }

      // ── Player ship ──
      const hd    = spatial.playerShip.heading;
      const angle = Math.atan2(hd.z, hd.x);

      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(angle);

      // Dashed heading line
      ctx.beginPath();
      ctx.moveTo(10, 0); ctx.lineTo(22, 0);
      ctx.strokeStyle = "rgba(120, 190, 255, 0.35)";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Ship chevron
      ctx.beginPath();
      ctx.moveTo(9, 0); ctx.lineTo(-5, 5); ctx.lineTo(-3, 0); ctx.lineTo(-5, -5);
      ctx.closePath();
      ctx.fillStyle = "rgba(180, 220, 255, 0.95)";
      ctx.fill();
      ctx.strokeStyle = "#aaddff";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();

      if (spatial.playerShip.shields === "up") {
        ctx.beginPath();
        ctx.arc(CX, CY, 11, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(100, 180, 255, 0.55)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // ── Header ──
      ctx.fillStyle = "rgba(80, 130, 200, 0.7)";
      ctx.font = "bold 7px 'Courier New', monospace";
      ctx.fillText("TACTICAL", 5, 10);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [getScale]);

  // ── Resize observer — keep canvas sized to container ──
  const wrapRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 200, h: 200 });

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setCanvasSize({ w: Math.round(width), h: Math.round(height) });
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="tactical-wrap">
      <canvas
        ref={canvasRef}
        width={canvasSize.w}
        height={canvasSize.h}
        className="tactical-canvas"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
      />
      {tooltip && (
        <div
          className="tactical-tooltip"
          style={{ left: tooltip.cx + 12, top: tooltip.cy - 10 }}
        >
          <div className="tt-name">{tooltip.name}</div>
          <div className="tt-stat">BRG {tooltip.bearing}°</div>
          <div className="tt-stat">RNG {tooltip.dist}u</div>
        </div>
      )}
    </div>
  );
}
